import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FaMapMarkerAlt, FaEdit, FaTruck } from 'react-icons/fa';
import API_CONFIG from './config';
import './styles/waste_carriers.css';

interface WasteCarrier {
    id: number;
    name: string;
    address: string;
    ticket_count?: number;
    sic_code: string;
    waste_carrier_license_num?: string;
    phone_number?: string;
}

const CARD_WIDTH = 350;
const CARD_HEIGHT = 340;

const WasteCarriers: React.FC = () => {
    const navigate = useNavigate();
    const [carriers, setCarriers] = useState<WasteCarrier[]>([]);
    const [checkingSession, setCheckingSession] = useState(true);
    const [showDialog, setShowDialog] = useState(false);
    const [name, setName] = useState('');
    const [address, setAddress] = useState('');
    const [sicCode, setSicCode] = useState('');
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editCarrier, setEditCarrier] = useState<WasteCarrier | null>(null);
    const [editName, setEditName] = useState('');
    const [editAddress, setEditAddress] = useState('');
    const [editSicCode, setEditSicCode] = useState('');
    const [licenseNumber, setLicenseNumber] = useState('');
    const [editLicenseNumber, setEditLicenseNumber] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [editPhoneNumber, setEditPhoneNumber] = useState('');

    useEffect(() => {
        let cancelled = false;
        setCheckingSession(true);

        // validate session first, then request carriers
        axios.get('/api/session', { withCredentials: true })
            .then(() => {
                return axios.get('/api/waste-carriers', { withCredentials: true, baseURL: API_CONFIG.BASE_URL });
            })
            .then(res => {
                if (cancelled) return;
                console.log('Waste carriers:', res.data);
                setCarriers(res.data);
            })
            .catch(err => {
                if (cancelled) return;
                if (err?.response?.status === 401) {
                    try { sessionStorage.setItem('sessionExpired', 'true'); } catch {}
                    navigate('/login');
                } else {
                    setCarriers([]);
                }
            })
            .finally(() => {
                if (!cancelled) setCheckingSession(false);
            });

        return () => { cancelled = true; };
    }, [showDialog, message, navigate]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setMessage('');
        if (!name.trim() || !address.trim() || !sicCode.trim() || !licenseNumber.trim()) {
            setError('All fields are required');
            return;
        }
        try {
            await axios.post('/api/waste-carriers', { name, address, sic_code: sicCode, license_number: licenseNumber }, { withCredentials: true, baseURL: API_CONFIG.BASE_URL });
            setMessage('Carrier created!');
            setName('');
            setAddress('');
            setSicCode('');
            setLicenseNumber('');
            setShowDialog(false);
        } catch (err: any) {
            if (err?.response?.status === 401) {
                try { sessionStorage.setItem('sessionExpired', 'true'); } catch {}
                navigate('/login');
                return;
            }
            setError(err.response?.data?.error || 'Failed to create carrier');
        }
    };

    const handleEditClick = (carrier: WasteCarrier) => {
        setEditCarrier(carrier);
        setEditName(carrier.name);
        setEditAddress(carrier.address);
        setEditSicCode(carrier.sic_code);
        setEditLicenseNumber(carrier.waste_carrier_license_num || '');
        setEditPhoneNumber(carrier.phone_number || '');
        setEditDialogOpen(true);
    };

    const handleEditSave = async () => {
        if (!editCarrier) return;
        try {
            await axios.put(`/api/waste-carriers/${editCarrier.id}`, {
                name: editName,
                address: editAddress,
                sic_code: editSicCode,
                license_number: editLicenseNumber,
                phone_number: editPhoneNumber
            }, { withCredentials: true, baseURL: API_CONFIG.BASE_URL });
            setMessage('Carrier updated!');
            setEditDialogOpen(false);
            setEditCarrier(null);
            axios.get('/api/waste-carriers', { withCredentials: true, baseURL: API_CONFIG.BASE_URL })
                .then(res => setCarriers(res.data))
                .catch(() => setCarriers([]));
        } catch (err: any) {
            if (err?.response?.status === 401) {
                try { sessionStorage.setItem('sessionExpired', 'true'); } catch {}
                navigate('/login');
                return;
            }
            setError(err.response?.data?.error || 'Failed to update carrier');
        }
    };

    return (
        <div
            style={{
                width: '100%',
                minHeight: '100vh',
                background: '#f5f6fa', // Same gray background as Projects
                display: 'flex',
                flexDirection: 'column',
                padding: '40px',
                boxSizing: 'border-box'
            }}
            className="waste-carriers-main-container"
        >
            {/* Header directly on gray background */}
            <div className="waste-carriers-header">
                <div className="waste-carriers-title">
                    <h2 style={{
                        margin: 0,
                        fontSize: '2.4em',
                        fontWeight: 700,
                        letterSpacing: '0.5px',
                        color: '#333' // Make sure text is visible on gray background
                    }}>
                        Waste Carriers
                    </h2>
                </div>
                <div className="create-button-container">
                    <button
                        style={{
                            background: '#115740',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 6,
                            padding: '10px 22px',
                            fontWeight: 600,
                            fontSize: '1em',
                            cursor: 'pointer',
                            boxShadow: '0 2px 8px rgba(17,87,64,0.3)'
                        }}
                        onClick={() => setShowDialog(true)}
                    >
                        + Add Carrier
                    </button>
                </div>
            </div>
            
            {/* Grid directly on gray background */}
            <div className="waste-carriers-grid">
                {carriers.length === 0 ? (
                    <div style={{ 
                        color: '#666', 
                        textAlign: 'center', 
                        padding: 32, 
                        width: '100%', 
                        gridColumn: '1/-1',
                        background: '#fff',
                        borderRadius: 12,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                    }}>
                        No carriers found.
                    </div>
                ) : (
                    Array.from({ length: Math.max(carriers.length, 4) }).map((_, idx) => {
                        const carrier = carriers[idx];
                        if (!carrier) {
                            return <div key={`empty-${idx}`} style={{ width: CARD_WIDTH, height: CARD_HEIGHT, visibility: 'hidden' }} />;
                        }
                        return (
                            <div 
                                key={carrier.id} 
                                className="waste-carrier-card"
                                style={{
                                    background: '#fff', // White cards on gray background
                                    borderRadius: 12,
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.12)', // Slightly stronger shadow for contrast
                                    padding: 28,
                                    width: CARD_WIDTH,
                                    height: CARD_HEIGHT,
                                    position: 'relative',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    textAlign: 'center',
                                    overflow: 'hidden',
                                    wordBreak: 'break-word'
                                }}
                            >
                                {/* Circle initials */}
                                <div 
                                    className="card-avatar"
                                    style={{
                                        width: 44,
                                        height: 44,
                                        borderRadius: '50%',
                                        background: '#115740',
                                        color: '#fff',
                                        fontWeight: 700,
                                        fontSize: '1.2em',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        position: 'absolute',
                                        top: 22,
                                        left: 22
                                    }}
                                >
                                    {carrier.name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0,2)}
                                </div>
                                {/* Edit icon only */}
                                <div style={{
                                    position: 'absolute',
                                    top: 22,
                                    right: 22,
                                    display: 'flex',
                                    gap: 16
                                }}>
                                    <FaEdit
                                        className="card-edit-icon"
                                        style={{ color: '#888', cursor: 'pointer', fontSize: '1.1em' }}
                                        title="Edit"
                                        onClick={() => handleEditClick(carrier)}
                                    />
                                </div>
                                <div style={{
                                    width: '90%',
                                    margin: '0 auto',
                                    position: 'relative',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    flex: 1,
                                    justifyContent: 'center'
                                }}>
                                    {/* Name */}
                                    <div 
                                        className="card-title"
                                        style={{
                                            fontWeight: 700,
                                            fontSize: '1.1em',
                                            color: '#115740',
                                            marginTop: 54,
                                            marginBottom: 10,
                                            wordBreak: 'break-word',
                                            whiteSpace: 'normal',
                                            overflow: 'visible',
                                            textOverflow: 'unset'
                                        }}
                                    >
                                        {carrier.name}
                                    </div>
                                    {/* Address */}
                                    <div 
                                        className="card-address"
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: 10,
                                            color: '#444',
                                            fontSize: '0.9em',
                                            marginBottom: 10,
                                            width: '100%',
                                        }}
                                    >
                                        <FaMapMarkerAlt
                                            style={{
                                                color: '#115740',
                                                fontSize: '1.1em',
                                                flexShrink: 0,
                                                alignSelf: 'flex-start',
                                                marginTop: 2
                                            }}
                                        />
                                        <span style={{
                                            textAlign: 'center',
                                            wordBreak: 'break-word',
                                            whiteSpace: 'pre-line',
                                            width: '100%',
                                            display: 'block'
                                        }}>
                                            {carrier.address}
                                        </span>
                                    </div>
                                    {/* SIC Code */}
                                    <div 
                                        className="card-sic-code"
                                        style={{
                                            color: '#115740',
                                            fontWeight: 600,
                                            fontSize: '1em',
                                            marginTop: 10,
                                            marginBottom: 10
                                        }}
                                    >
                                        SIC Code: {carrier.sic_code}
                                    </div>
                                    {/* License Number */}
                                    <div 
                                        className="card-license"
                                        style={{
                                            color: '#444',
                                            fontWeight: 600,
                                            fontSize: '1em',
                                            marginBottom: 10
                                        }}
                                    >
                                        License Number: {carrier.waste_carrier_license_num ?? '—'}
                                    </div>
                                    {/* Phone Number */}
                                    <div 
                                        className="card-phone"
                                        style={{
                                            color: '#444',
                                            fontWeight: 600,
                                            fontSize: '1em',
                                            marginBottom: 10
                                        }}
                                    >
                                        Phone Number: {carrier.phone_number ?? '—'}
                                    </div>
                                    {/* Divider */}
                                    <hr style={{ width: '70%', margin: '18px auto 12px auto', border: 'none', borderTop: '1px solid #eee' }} />
                                    <div 
                                        className="card-ticket-count"
                                        style={{
                                            color: '#115740',
                                            fontWeight: 600,
                                            fontSize: '1em',
                                            marginTop: 10,
                                            marginBottom: 10
                                        }}
                                    >
                                        Waste Tickets: {carrier.ticket_count ?? 0}
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            {/* Edit Dialog */}
            {editDialogOpen && editCarrier && (
                <div style={{
                    position: 'fixed',
                    top: 0, left: 0, width: '100vw', height: '100vh',
                    background: 'rgba(0,0,0,0.18)', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', zIndex: 1000
                }}>
                    <div className="carrier-edit-dialog" style={{
                        background: '#fff', padding: '32px', borderRadius: '12px',
                        boxShadow: '0 2px 12px rgba(0,0,0,0.12)', minWidth: 320
                    }}>
                        <h3 style={{ marginBottom: 18 }}>Edit Carrier</h3>
                        <form style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                            <input
                                type="text"
                                placeholder="Carrier Name"
                                value={editName}
                                onChange={e => setEditName(e.target.value)}
                                required
                            />
                            <input
                                type="text"
                                placeholder="Address"
                                value={editAddress}
                                onChange={e => setEditAddress(e.target.value)}
                                required
                            />
                            <input
                                type="text"
                                placeholder="SIC Code"
                                value={editSicCode}
                                onChange={e => setEditSicCode(e.target.value)}
                                required
                            />
                            <input
                                type="text"
                                placeholder="Waste Carrier License Number"
                                value={editLicenseNumber}
                                onChange={e => setEditLicenseNumber(e.target.value)}
                                required
                            />
                            <input
                                type="text"
                                placeholder="Waste Carrier Phone Number"
                                value={editPhoneNumber}
                                onChange={e => setEditPhoneNumber(e.target.value)}
                            />
                            <div style={{ display: 'flex', gap: 12 }}>
                                <button
                                    type="button"
                                    style={{
                                        padding: '10px 22px',
                                        borderRadius: 6,
                                        background: '#115740',
                                        color: '#fff',
                                        border: 'none',
                                        fontWeight: 600
                                    }}
                                    onClick={handleEditSave}
                                >
                                    Save
                                </button>
                                <button
                                    type="button"
                                    style={{
                                        padding: '10px 22px',
                                        borderRadius: 6,
                                        background: '#eee',
                                        color: '#333',
                                        border: 'none',
                                        fontWeight: 500
                                    }}
                                    onClick={() => {
                                        setEditDialogOpen(false);
                                        setEditCarrier(null);
                                    }}
                                >
                                    Cancel
                                </button>
                            </div>
                            {error && <div style={{ color: '#d32f2f', fontWeight: 500 }}>{error}</div>}
                        </form>
                    </div>
                </div>
            )}

            {/* Create Dialog */}
            {showDialog && (
                <div style={{
                    position: 'fixed',
                    top: 0, left: 0, width: '100vw', height: '100vh',
                    background: 'rgba(0,0,0,0.18)', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', zIndex: 1000
                }}>
                    <div className="carrier-dialog" style={{
                        background: '#fff', padding: '32px', borderRadius: '12px',
                        boxShadow: '0 2px 12px rgba(0,0,0,0.12)', minWidth: 320
                    }}>
                        <h3 style={{ marginBottom: 18 }}>Create Waste Carrier</h3>
                        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                            <input
                                type="text"
                                placeholder="Carrier Name"
                                value={name}
                                onChange={e => setName(e.target.value)}
                                style={{ padding: 12, borderRadius: 6, border: '1px solid #ccc' }}
                                required
                            />
                            <input
                                type="text"
                                placeholder="Address"
                                value={address}
                                onChange={e => setAddress(e.target.value)}
                                style={{ padding: 12, borderRadius: 6, border: '1px solid #ccc' }}
                                required
                            />
                            <input
                                type="text"
                                placeholder="SIC Code"
                                value={sicCode}
                                onChange={e => setSicCode(e.target.value)}
                                style={{ padding: 12, borderRadius: 6, border: '1px solid #ccc' }}
                                required
                            />
                            <input
                                type="text"
                                placeholder="Waste Carrier License Number"
                                value={licenseNumber}
                                onChange={e => setLicenseNumber(e.target.value)}
                                style={{ padding: 12, borderRadius: 6, border: '1px solid #ccc' }}
                                required
                            />
                            <input
                                type="text"
                                placeholder="Waste Carrier Phone Number"
                                value={phoneNumber}
                                onChange={e => setPhoneNumber(e.target.value)}
                                style={{ padding: 12, borderRadius: 6, border: '1px solid #ccc' }}
                            />
                            <div style={{ display: 'flex', gap: 12 }}>
                                <button type="submit" style={{
                                    padding: '10px 22px',
                                    borderRadius: 6,
                                    background: '#115740',
                                    color: '#fff',
                                    border: 'none',
                                    fontWeight: 600
                                }}>
                                    Create Carrier
                                </button>
                                <button type="button" style={{
                                    padding: '10px 22px',
                                    borderRadius: 6,
                                    background: '#eee',
                                    color: '#333',
                                    border: 'none',
                                    fontWeight: 500
                                }} onClick={() => setShowDialog(false)}>
                                    Cancel
                                </button>
                            </div>
                            {message && <div style={{ color: '#115740', fontWeight: 500 }}>{message}</div>}
                            {error && <div style={{ color: '#d32f2f', fontWeight: 500 }}>{error}</div>}
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default WasteCarriers;